import { isSameMm } from '@repo/api-contracts';

const DATE_TIME_FORMATTER = new Intl.DateTimeFormat('ko-KR', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

/** Date 또는 ISO 문자열을 한국어 로캘 문자열로 변환한다. */
export const formatDateTime = (value: Date | string): string => {
  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return DATE_TIME_FORMATTER.format(date);
};

const KO_NUMBER_FORMATTER = new Intl.NumberFormat('ko-KR');

/** 원 단위 정수를 "8,900원" 형태로 바꾼다 — 결제·환불 금액 표시. */
export const formatWon = (amount: number): string => `${KO_NUMBER_FORMATTER.format(amount)}원`;

/** 수량을 "12,000" 형태로 바꾼다 — 단위("개")는 호출부가 붙인다. */
export const formatCount = (value: number): string => KO_NUMBER_FORMATTER.format(value);

const MM_FRACTION_DIGITS = 2;
/** 바뀐 값의 표시가 갈릴 때까지 늘리는 소수 자리 상한 — Intl.NumberFormat 이 어디서나 받는 최대값이다. */
const MAX_MM_FRACTION_DIGITS = 20;

const mmFormatter = (fractionDigits: number): Intl.NumberFormat =>
  new Intl.NumberFormat('ko-KR', { maximumFractionDigits: fractionDigits, useGrouping: false });

const MM_FORMATTER = mmFormatter(MM_FRACTION_DIGITS);

/**
 * mm 값을 소수 둘째 자리까지(끝자리 0 없이) 보인다 — 상품 실측(DECIMAL(9,2))과 같은 자릿수다.
 * 화면 표시 전용이다. 원통 프레임 폭처럼 반올림하면 달라지는 값을 이 문자열로 되돌려 저장하지 않는다.
 */
export const formatMm = (value: number): string => MM_FORMATTER.format(value);

const AREA_MM_FORMATTER = mmFormatter(1);

/**
 * 인쇄 영역 크기·위치를 소수 첫째 자리까지(끝자리 0 없이) 보인다 — 사진에서 그려 소수가 긴 값(62.5279512)을
 * 운영자에게 `62.5` 로 보인다. 화면 표시 전용이다. 이 문자열을 저장값으로 되돌리지 않는다.
 */
export const formatAreaMm = (value: number): string => {
  const text = AREA_MM_FORMATTER.format(value);
  // 0 으로 반올림되는 작은 음수(−0.04)는 '-0' 이 아니라 '0' 으로 보인다.
  return text === '-0' ? '0' : text;
};

/** 두 값의 표시가 처음 갈리는 소수 자리 수(둘째 자리부터). */
const fractionDigitsApart = (from: number, to: number): number => {
  for (let digits = MM_FRACTION_DIGITS; digits < MAX_MM_FRACTION_DIGITS; digits += 1)
    if (mmFormatter(digits).format(from) !== mmFormatter(digits).format(to)) return digits;
  return MAX_MM_FRACTION_DIGITS;
};

/**
 * 바뀌기 전·후 mm 값을 formatMm 처럼 보인다. 두 값이 계약 허용오차(isSameMm·GEOMETRY_TOLERANCE)를 넘게 다른데
 * 표시가 같으면(반올림해 저장된 114.6681 → 114.66813…) 차이가 보일 때까지 소수 자리를 늘린다 — 바뀐 값이
 * '114.67 → 114.67' 처럼 그대로인 것으로 보이지 않게 한다. 허용오차 안의 차이는 같은 값으로 보인다 — drift 판정과
 * 같은 함수로 비교해 '다르다고 거부되는데 같아 보이는' 값이 없다. 화면 표시 전용이다.
 */
export const formatMmChange = (from: number, to: number): readonly [string, string] => {
  const formatter = mmFormatter(
    isSameMm(from, to) ? MM_FRACTION_DIGITS : fractionDigitsApart(from, to),
  );
  return [formatter.format(from), formatter.format(to)];
};

const HANGUL = /[가-힣]/;

/**
 * 화면에 보일 검증 문구. 계약이 붙인 한국어 문구는 그대로 보이고, 한글이 없는 문구(zod 기본 영어 문구 등)나
 * 문구가 없으면 그 자리의 한국어 대체 문구를 보인다 — 'Too small: …' 같은 영어 문구가 화면에 그대로 나가지 않게 한다.
 */
export const koreanMessageOr = (message: string | undefined, fallback: string): string =>
  message !== undefined && HANGUL.test(message) ? message : fallback;
